/**
 * The single sim-side assembler that turns *requested* cosmetic layers into a
 * validated {@link EquippedItem} render stack. Both equipment entry points feed
 * it: the server's connect-time `coerceEquipment` (after it splits its
 * `<id>:<hue>` query tokens) and the roster's {@link npcEquipment} (after it maps
 * an {@link NpcAppearance} struct), so the body-gating, hue-clamp,
 * one-item-per-layer and canonical-ordering rules live in exactly one place and
 * can never drift between the player and NPC paths.
 *
 * It lives in `sim/` (not `server/`) so both callers can share it without
 * inverting the dependency direction - the server already imports the sim, never
 * the reverse (see ADR-0030). It holds the same "never throw, always yield
 * something drawable" contract both callers documented.
 */
import { equipment } from './content';
import type { EquippedItem } from './entity';

/**
 * A cosmetic layer *before* validation: a registry id and an optional hue, in
 * the order the caller wants layers resolved (first item per render layer wins).
 * Structurally the same `{ id, hue? }` shape an `NpcAppearanceLayer` carries, so
 * a roster struct's fields drop straight in.
 */
export interface RequestedLayer {
  /** `equipment` registry id, e.g. `hair.long`; unknown ids are dropped. */
  id: string;
  /** Desired hue; kept only when a positive integer, else raw grayscale. */
  hue?: number;
}

/**
 * Assemble `requested` layers into the validated equipment stack for `body`:
 *
 * - an `id` not in the {@link equipment} content registry is **dropped** (a
 *   caller can't conjure a layer the converter never baked);
 * - a layer whose `bodies` excludes `body` is **dropped** - notably a beard
 *   (`bodies: [400]`) on a 401 body, mirroring the render-stack gender gate;
 * - a `hue` is kept only when it is a positive integer, else the layer renders
 *   in raw grayscale;
 * - at most one item per render layer survives (the first requested wins);
 * - survivors are emitted in the registry's canonical layer order, so an
 *   unchanged loadout is byte-stable at spawn and never spuriously marks an
 *   entity `changed` in the delta.
 *
 * Never throws: a malformed or empty request degrades to a bare body (`[]`).
 */
export function assembleEquipment(requested: Iterable<RequestedLayer>, body: number): EquippedItem[] {
  const byLayer = new Map<string, EquippedItem>();
  for (const { id, hue } of requested) {
    if (!equipment.has(id)) continue; // unknown id: drop, never throw
    const def = equipment.get(id);
    if (def.bodies && !def.bodies.includes(body)) continue; // body-gated layer (e.g. beard on 401)
    if (byLayer.has(def.layer)) continue; // one item per render layer: first wins
    const cleanHue = Number.isInteger(hue) && (hue ?? 0) > 0 ? hue : undefined;
    byLayer.set(def.layer, cleanHue === undefined ? { id, layer: def.layer } : { id, layer: def.layer, hue: cleanHue });
  }
  const layerOrder = [...new Set(equipment.all().map((def) => def.layer))];
  return layerOrder
    .map((layer) => byLayer.get(layer))
    .filter((item): item is EquippedItem => item !== undefined);
}
